<template>
  <Modal
    ref="productImagesModalRef"
    :show-icon="false"
    :click-to-close="true"
    :modal-style="{ width: '992px'}"
    class="request-update-modal"
    positive-action-text="Talep Oluştur"
    negative-action-text="Vazgeç"
    :isPositiveActionButtonDisabled="isSubmitting || !description || description.trim() === ''"
    @onPositiveActionClick="handleSubmit"
    @onNegativeActionClick="closeModal"
    @modalClosed="handleModalClosed"
  >
    <template v-slot:body>
      <div class="header">
        <p class="title">
          Ürün Görselleri için Güncelleme Talep Et/Hata Bildir
        </p>
      </div>
      <div class="scrollable-content">
        <!-- Bilgilendirme Mesajı -->
        <div class="info-banner">
          <Icons name="InfoFilledIcon" size="md" color="#4FA4E5" />
          <span class="info-text">
            Dilerseniz yeni ürün görseli yükleyebilir veya Talep Açıklaması girerek düzenleme talebinde bulunabilirsiniz.
          </span>
        </div>

        <!-- Mevcut Ürün Görselleri -->
        <div v-if="props.images && props.images.length > 0" class="current-images-section">
          <div class="current-images-title">Mevcut Ürün Görselleri</div>
          <div class="images-wrapper">
            <button 
              v-if="showLeftArrow && props.images.length > 8" 
              class="scroll-btn left" 
              @click="scrollImages(-1)"
            >
              <img src="@/assets/images/icons-arrow-1-right.svg" alt="Sol" />
            </button>
            <div ref="imagesGridRef" class="images-grid" @scroll="onScroll">
              <div
                v-for="(img, idx) in props.images"
                :key="idx"
                class="image-thumb"
                :class="{ cover: img.isCover }"
              >
              <img
                v-if="!needsPlaceholder(img, idx)"
                :src="img?.src"
                alt="ürün görseli"
                @error="onImgError(idx)"
              />

              <img
                v-else
                src="@/assets/images/icons/image-placeholder.svg"
                alt="ürün görseli"
              />
              <div v-if="img?.isCover" class="cover-label">Kapak Görseli</div>
              </div>
            </div>
            <button 
              v-if="props.images.length > 8" 
              class="scroll-btn right" 
              @click="scrollImages(1)"
            >
              <img src="@/assets/images/icons-arrow-1-right.svg" alt="Sağ" />
            </button>
          </div>
        </div>

        <!-- Görsel Yükleme Alanı -->
        <div>
          <div class="image-upload-header-section">
            <div class="image-upload-title">Talep Ettiğiniz Ürün Görselleri ({{ selectedImages.length }}/8)</div>
          </div>
          
          <div 
            v-if="selectedImages.length === 0 && failedFiles.length === 0" 
            class="image-upload-area"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
            <img src="@/assets/images/image-upload@2x.png" />
            <div class="image-upload-header">Görsel Ekle</div>
            <span class="image-upload-description">Ürün görsellerini yüklemek için bilgisayarınızdan seçin veya buraya sürükleyip bırakın.</span>
            <span class="image-upload-description-note">(En fazla 8 görsel yükleyebilirsiniz.)</span>
            <Button primary md class="image-upload-button" @click="handleFileSelect">
              Dosya Seç
            </Button>
            <span class="image-upload-note">Görseller <strong>.jpeg, .jpg, .png</strong> formatında ve en fazla <strong>10 MB</strong> boyutunda olmalıdır.</span>
          </div>

          <div v-else class="selected-images-area">
            <div class="selected-images-grid">
              <div
                v-for="(image, index) in selectedImages"
                :key="image.key"
                class="image-item"
              >
                <img class="image-item-image" :src="image.image" :alt="image.title" />
                <button class="remove-image-btn" @click="removeImage(index)">
                  <Icons name="Delete" size="sm" color="#FF44EE" />
                </button>
                <div class="image-info">
                  <div class="image-title">{{ image.title }}</div>
                  <div class="image-status">
                    <span class="ready-status"></span>
                    <span class="image-status-text">Yüklendi</span>
                  </div>
                </div>
              </div>
              <div
                v-for="(failedFile, index) in failedFiles"
                :key="failedFile.key"
                class="image-item failed"
              >
                <img class="failed-image-item" src="@/assets/images/icons/image-placeholder.svg" :alt="failedFile.fileName" />
                <button class="remove-image-btn" @click="deleteFailedImage(failedFile.key)">
                  <Icons name="Delete" size="sm" color="#FF44EE" />
                </button>
                <div class="failed-image-info">
                  <div class="failed-image-title">{{ failedFile.fileName }}</div>
                  <div class="failed-image-status">
                    <img
                      width="16px"
                      height="16px"
                      src="@/assets/images/icons/alert-circle.svg"
                      alt="Error Icon"
                    />
                    <span>Yüklenemedi</span>
                  </div>
                  <div class="failed-image-description" v-html="failedFile.errorMessage"></div>
                </div>
              </div>
              <div
                v-if="selectedImages.length + failedFiles.length < 8"
                class="add-image-card"
                @click="handleFileSelect"
                @dragover.prevent
                @drop.prevent="onDrop"
              >
                <img src="@/assets/images/icons/plus.svg" width="24px" height="24px" />
                <span class="add-image-text">Görsel Ekle</span>
                <span class="add-image-subtext">Bilgisayardan seçin veya sürükleyip bırakın.</span>
              </div>
              <div
                v-for="placeholderIndex in (selectedImages.length + failedFiles.length < 8 ? (7 - selectedImages.length - failedFiles.length) : 0)"
                :key="placeholderIndex"
                class="placeholder-card"
              >
              </div>
            </div>
          </div>
        </div>

        <input
          ref="fileInput"
          type="file"
          @change="onFileChange"
          hidden
          accept="image/jpeg,image/png,image/jpg,image/webp,application/pdf"
          multiple
        />

       <!-- Talep Açıklaması -->
        <div class="form-group description-group" :class="{ 'has-error': descriptionError }">
            <div class="description-header">
                <label for="request-description" class="description-title">Talep Açıklaması</label>
                <span class="optional-badge">*ZORUNLU</span>
            </div>
            <Input
                id="request-description"
                type="textarea"
                v-model:value="description"
                label="Talebinizi yazın"
                :rows="4"
                size="md"
                @input="descriptionError = false"
            />
            <div v-if="descriptionError" class="error-message">
              Talep açıklaması yazın.
            </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref } from "vue";
import { useStore } from "vuex";
import { Modal, Icons, Input, Button } from "@n11/so-ui-components";
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const store = useStore();

const props = defineProps({
  images: {
    type: Array,
    default: () => [],
  },
  groupId: {
    type: Number,
    default: "",
  },
});

const productImagesModalRef = ref(null);
const description = ref("");
const selectedImages = ref([]);
const fileInput = ref(null);
const failedFiles = ref([]);
const descriptionError = ref(false);
const imagesGridRef = ref(null);
const showLeftArrow = ref(false);
const isSubmitting = ref(false);
const skipDismissOnClose = ref(false);
const brokenIndexes = ref([]);

const needsPlaceholder = (img, idx) => {
  if (!img?.src) return true;
  return brokenIndexes.value.includes(idx);
};

const onImgError = (idx) => {
  if (!brokenIndexes.value.includes(idx)) {
    brokenIndexes.value.push(idx);
  }
};

const handleFileSelect = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

const handleSubmit = async () => {
  if (!description.value || description.value.trim() === "") {
    descriptionError.value = true;
    return;
  }

  if (isSubmitting.value) return;

  descriptionError.value = false;
  isSubmitting.value = true;

  try {
    let imageUrls = [];
    
    if (selectedImages.value.length > 0) {
      const formData = new FormData();
      selectedImages.value.forEach((img) => {
        formData.append('images', img.file);
      });

      const uploadedImages = await store.dispatch("products/uploadProductImages", {
        productImages: formData,
      });
      
      imageUrls = uploadedImages?.map(img => img.url) || [];
    }

    const payload = {
      groupId: props.groupId,
      description: description.value,
      imageUrls: imageUrls,
    };

    await store.dispatch(
      "products/postProductFeedbackSuggestionsImage",
      payload
    );
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_SUCCESS, {
      button_name: 'Talep Oluştur',
      button_type: 'Request Success',
      button_area: 'Product Image',
    });

    store.commit("common/SET_TOASTER_DATA", {
      type: "success",
      title: "Başarılı",
      description: "Talep başarıyla oluşturuldu",
    });

    skipDismissOnClose.value = true;
    closeModal();
  } catch (error) {
    let errorMessage = error.data.errors && error.data.errors[0] ? error.data.errors[0].message : "Bir hata oluştu. Lütfen tekrar deneyin.";

    store.commit("common/SET_TOASTER_DATA", {
      type: "error",
      title: "Hata",
      description: errorMessage,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const onFileChange = (event) => {
  const files = event.target.files;
  if (files && files.length > 0) {
    addImagesFromFileList(files);
    event.target.value = "";
  }
};

const onDrop = (event) => {
  const files = event.dataTransfer.files;
  if (files && files.length > 0) {
    addImagesFromFileList(files);
  }
};

const addImagesFromFileList = (files) => {
  const maxFiles = 8;
  const currentCount = selectedImages.value.length;
  const remainingSlots = maxFiles - currentCount;

  const maxTotalSize = 10 * 1024 * 1024; // 10 MB total for all images
  const currentTotalSize = selectedImages.value.reduce((sum, img) => {
    return sum + (img.file?.size || 0);
  }, 0);
  let reservedSize = 0;

  Array.from(files)
    .slice(0, remainingSlots)
    .forEach((file) => {
      let errorMessage = "";

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        errorMessage = `Dosya uzantısı <span style="font-weight: 700;">jpeg, jpg</span> veya <span style="font-weight: 700;">png</span> formatında olmalıdır.`;
      }

      const prospectiveTotal = currentTotalSize + reservedSize + file.size;
      if (!errorMessage && prospectiveTotal > maxTotalSize) {
        errorMessage = `Dosya boyutu en fazla <span style="font-weight: 700;">10 MB</span> olmalıdır.`;
      }

      const objectUrl = URL.createObjectURL(file);

      if (errorMessage) {
        failedFiles.value.push({
          errorMessage: errorMessage,
          file: file,
          fileName: file.name,
          key: `${file.name}-${Date.now()}`,
          image: objectUrl,
        });
        return;
      }

      reservedSize += file.size;

      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 5000 || img.naturalHeight > 5000) {
          reservedSize -= file.size;
          failedFiles.value.push({
            errorMessage: `Resim boyutu en fazla <span style="font-weight: 700;">5000px</span> olmalıdır.`,
            file: file,
            fileName: file.name,
            key: `${file.name}-${Date.now()}`,
            image: objectUrl,
          });
          return;
        }

        selectedImages.value.push({
          file: file,
          key: `${file.name}-${Date.now()}`,
          fileName: file.name,
          title: file.name,
          image: objectUrl,
        });
      };
      
      img.onerror = () => {
        reservedSize -= file.size;
        failedFiles.value.push({
          errorMessage: "Görsel okunamadı.",
          file: file,
          fileName: file.name,
          key: `${file.name}-${Date.now()}`,
          image: objectUrl,
        });
      };
      
      img.src = objectUrl;
    });
};

const removeImage = (index) => {
  const deletedImage = selectedImages.value[index];
  
  if (deletedImage?.image) {
    URL.revokeObjectURL(deletedImage.image);
  }
  
  selectedImages.value.splice(index, 1);
};

const deleteFailedImage = (key) => {
  const index = failedFiles.value.findIndex((file) => file.key === key);
  if (index !== -1) {
    if (failedFiles.value[index]?.image) {
      URL.revokeObjectURL(failedFiles.value[index].image);
    }
    failedFiles.value.splice(index, 1);
  }
};

const openModal = () => {
  productImagesModalRef.value?.openModal();
};

const closeModal = () => {
  description.value = "";
  selectedImages.value = [];
  failedFiles.value = [];
  descriptionError.value = false;
  isSubmitting.value = false;
  productImagesModalRef.value?.hiddenModal();
};

const handleModalClosed = () => {
  if (!skipDismissOnClose.value) {
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Request Dismiss',
      button_area: 'Product Image',
    });
  }
  skipDismissOnClose.value = false;
};

const scrollImages = (direction) => {
  if (!imagesGridRef.value) return;
  
  const scrollAmount = 112;
  imagesGridRef.value.scrollBy({
    left: direction * scrollAmount,
    behavior: 'smooth'
  });
};

const onScroll = () => {
  if (!imagesGridRef.value) return;
  showLeftArrow.value = imagesGridRef.value.scrollLeft > 0;
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";

.image-upload-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  width: 944px;
  color: $colorN80;
  background-color: $colorM05;
  border: 2px dashed $colorM50;
  padding: 24px;
  margin: 16px 0px 24px 0px;

  img {
    width: 90px;
    height: 90px;
  }

  .image-upload-button {
    width: 100px;
    margin-top: 16px;
  }

  .image-upload-header {
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: -0.1px;
    color: $colorN80;
    margin: 12px 0px 8px 0px;
  }

  .image-upload-description {
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;
  }
}

.image-upload-header-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.image-upload-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.05px;
  text-align: left;
}

.selected-images-area {
  margin-top: 16px;
}

.selected-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  border-radius: 8px;
  min-height: 150px;
  padding-bottom: 16px;
}

.image-upload-note {
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  color: $colorN70;
  strong {
    font-weight: bold;
  }
}

.image-upload-description-note {
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: -0.05px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 261px;
  border-radius: 8px;
  overflow: visible;
  border: 1px solid $colorD20;
  background: $colorN00;

  .image-item-image {
    width: 100%;
    height: 140px;
    object-fit: contain;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .remove-image-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: $colorN00;
    border: 1px solid $colorD20;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0;
    z-index: 2;

    div {
      width: 16px;
      height: 16px;
    }
  }

  .image-info {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .image-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: $colorN80;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 84px;
  }

  .image-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 12px;
    color: $colorS50;
  }

  .ready-status {
    display: block;
    width: 12px;
    height: 12px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Ccircle cx='6' cy='6' r='6' fill='%23008C20'/%3E%3Cpath d='M8.39961 4.2002L5.39961 7.8002L3.59961 6.0002' stroke='white' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    flex-shrink: 0;
  }

  .image-status-text {
    font-size: 10px;
    font-weight: 600;
    line-height: 12px;
    color: $colorS50;
  }

  &.failed {
    .failed-image-item {
      width: 98px;
      height: 140px;
      border-bottom: 0.5px solid $colorD20;
      border-radius: 8px 8px 0px 0px;
    }

    .failed-image-info {
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .failed-image-title {
      font-size: 10px;
      font-weight: 600;
      line-height: 16px;
      color: $colorN80;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 84px;
    }

    .failed-image-status {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
      color: $colorE60;
    }

    .failed-image-description {
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
      color: $colorE60;
      margin-top: 4px;
      text-align: left;
    }
  }
}

.add-image-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 261px;
  border-radius: 8px;
  border: 2px dashed $colorM50;
  background: $colorM05;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 12px;
  gap: 8px;

  .add-image-text {
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: $colorM50;
    text-align: center;
  }

  .add-image-subtext {
    font-size: 11px;
    font-weight: 400;
    line-height: 14px;
    color: $colorN80;
    text-align: center;
  }
}

.placeholder-card {
  width: 100px;
  height: 261px;
  border-radius: 8px;
  background: $colorN10;
  border: 1px solid $colorN20;
}

.request-update-modal {
  :deep(.modal-container) {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  :deep(.modal-header) {
    position: absolute;
    top: 7px;
    right: 4px;
  }

  :deep(.modal-body) {
    padding: 0 !important;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden !important;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden !important;
      gap: 0 !important; 
    }
  }

  :deep(.modal-footer) {
    padding: 8px 12px 0 0 !important;
  }
}

.header {
  margin-bottom: 16px;

  .title {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.2px;
    text-align: left;
  }
}

.scrollable-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $colorN10;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: $colorN40;
    border-radius: 3px;

    &:hover {
      background: $colorN50;
    }
  }
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: $colorB05;
  border-radius: 6px;
  margin-bottom: 16px;

  .info-text {
    font-size: 13px;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: $colorN80;
  }
}

.current-images-section {
  margin-bottom: 16px;

  .current-images-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.05px;
    color: $colorN80;
    text-align: left;
    margin-bottom: 12px;
  }

  .images-wrapper {
    position: relative;
  }

  .scroll-btn {
    position: absolute;
    top: 35px;
    z-index: 10;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: $colorN00;
    border: 1px solid $colorD20;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &.left {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.20);
      left: 5px;
      width: 40px;
      height: 40px;
      
      img {
        transform: scaleX(-1);
      }
    }

    &.right {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.20);
      width: 40px;
      height: 40px;
      right: 5px;
    }

    img {
      width: 25px;
      height: 25px;
    }

    &:hover {
      background: $colorN10;
    }
  }

  .images-grid {
    display: flex;
    gap: 12px;
    padding-bottom: 16px;
    border-bottom: 0.5px solid $colorN30;
    overflow-x: auto;
    scroll-behavior: smooth;

    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .image-thumb {
    width: 100px;
    height: 100px;
    min-width: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid $colorD20;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $colorN00;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
    }

    .cover-label {
      position: absolute;
      top: 76px;
      width: 100%;
      text-align: center;
      background: $colorN80;
      color: $colorN00;
      font-size: 11px;
      font-weight: 700;
      line-height: 14px;
      padding: 4px 10px;
    }
  }
}

.form-group.description-group {
  margin-top: 4px;

  :deep(.input-container) {
    margin-bottom: 0;
  }

  &.has-error {
    :deep(textarea) {
      border: 1px solid $colorE50 !important;
      outline: 3px solid $colorE10 !important;
    }
  }

  .description-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .description-title {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .optional-badge {
      font-size: 10px;
      font-weight: 800;
      line-height: normal;
      padding: 0 8px;
      color: $colorM50;
      border-radius: 3px;
      border: 0.5px solid $colorM50;
    }
  }

  .error-message {
    color: $colorE50;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    margin-top: 4px;
  }
}
</style>